import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MessageSquarePlus, Users } from "lucide-react";
import ContributionCard from "@/components/community/ContributionCard";
import ContributionModal from "@/components/community/ContributionModal";

const ALL = "all";

export default function CommunitySection({ moduleId, moduleTitle }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState(ALL);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (cat) => {
    setLoading(true);
    try {
      const url = cat && cat !== ALL
        ? `/contributions/module/${moduleId}?category=${cat}`
        : `/contributions/module/${moduleId}`;
      const { data } = await api.get(url);
      setItems(data);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    api.get("/contributions/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const openModal = () => {
    if (!user || user === false) { nav("/login"); return; }
    setModalOpen(true);
  };

  const onUpvote = async (item) => {
    try {
      const { data } = await api.post(`/contributions/${item.id}/upvote`);
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, has_upvoted: data.has_upvoted, upvote_count: data.upvote_count } : x));
    } catch (e) {
      if (e?.response?.status === 401) { nav("/login"); return; }
      toast.error(formatApiError(e));
    }
  };
  const onRemoveUpvote = async (item) => {
    try {
      const { data } = await api.delete(`/contributions/${item.id}/upvote`);
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, has_upvoted: data.has_upvoted, upvote_count: data.upvote_count } : x));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const onDelete = async (item) => {
    if (!window.confirm("Delete this contribution? This cannot be undone.")) return;
    try {
      await api.delete(`/contributions/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section data-testid="community-section" className="max-w-4xl mx-auto px-6 pt-6 pb-16 border-t border-[#E5E5E0] mt-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-2">
            <Users className="w-3.5 h-3.5" /> Community insights
          </div>
          <h2 className="font-serif text-3xl font-black text-[#0A0A0A] tracking-tight">
            From people who actually bought.
          </h2>
          <p className="text-sm text-[#525252] mt-2 max-w-xl">
            Firsthand experiences from investors and recent buyers. Most upvoted show first.
          </p>
        </div>
        <button
          data-testid="open-contribute-modal"
          onClick={openModal}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#0A0A0A] text-white hover:bg-black transition-colors font-medium text-sm btn-primary-hover self-start md:self-auto"
        >
          <MessageSquarePlus className="w-4 h-4" /> Share your experience
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="community-filters">
        {[{ id: ALL, label: "All" }, ...categories].map((c) => (
          <button
            key={c.id}
            data-testid={`community-filter-${c.id}`}
            onClick={() => setFilter(c.id)}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
              filter === c.id
                ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                : "border-[#E5E5E0] text-[#525252] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="community-empty" className="border border-dashed border-[#E5E5E0] p-10 text-center bg-[#F5F5F0]">
          <p className="text-[#525252] mb-4">No contributions yet in this category. Be the first to share what worked (and what didn't).</p>
          <button
            onClick={openModal}
            className="px-4 py-2 bg-[#0A0A0A] text-white text-sm hover:bg-black transition-colors"
          >
            Share your experience
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <ContributionCard key={it.id} item={it} onUpvote={onUpvote} onRemoveUpvote={onRemoveUpvote} onDelete={onDelete} />
          ))}
        </div>
      )}

      <ContributionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        moduleId={moduleId}
        moduleTitle={moduleTitle}
        onCreated={(created) => setItems((prev) => [created, ...prev])}
      />
    </section>
  );
}
